import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Button,
  Table,
  Input,
  FormControl,
  FormLabel,
  Alert,
  Card,
  Select,
  Option,
  Textarea
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { formatDisplayDate } from '../util/formatDate';
import StockDetailLayout from './StockDetailLayout';
import ResponsiveDetailTable from './ResponsiveDetailTable';
import DetailFieldCard from './DetailFieldCard';
import VendorSelect from './VendorSelect';
import AddVendorModal from './AddVendorModal';

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_LABELS = {
  in_stock: 'In Stock',
  in_use: 'In Use',
  missed: 'Missed',
  broken: 'Broken',
  discarded: 'Discarded'
};

const ALLOWED_TO = {
  in_stock: ['in_use', 'missed', 'broken', 'discarded'],
  in_use: ['in_stock', 'missed', 'broken', 'discarded'],
  missed: ['discarded'],
  broken: ['discarded'],
  discarded: []
};

const InstrumentPopup = ({ instrumentName, onClose, variant = 'modal' }) => {
  const isPage = variant === 'page';
  const [batches, setBatches] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [showAddStock, setShowAddStock] = useState(false);
  const [addStockForm, setAddStockForm] = useState({
    purchase_date: today(),
    quantity: '',
    cost: '',
    brand: '',
    make: '',
    serial_no: '',
    vendor_id: ''
  });
  const [addStockSubmitting, setAddStockSubmitting] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [statusModalBatch, setStatusModalBatch] = useState(null);
  const [statusForm, setStatusForm] = useState({
    fromStatus: 'in_stock',
    toStatus: 'in_use',
    quantity: '',
    changeDate: today(),
    remarks: ''
  });
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [historyDeleteConfirm, setHistoryDeleteConfirm] = useState(null);

  const fetchHistory = useCallback(async (batchList) => {
    try {
      const responses = await Promise.all(
        batchList.map((b) => axiosInstance.get(`/instruments/${b.id}/history`))
      );
      const all = responses.flatMap((res, i) => {
        const list = res.data.history || [];
        return list.map((h) => ({ ...h, batch_id: batchList[i]?.id }));
      });
      all.sort((a, b) => new Date(b.change_date) - new Date(a.change_date));
      setHistory(all);
    } catch (err) {
      console.error('Failed to fetch history', err);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      setVendorsLoading(true);
      const res = await axiosInstance.get('/vendors');
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error('Failed to fetch vendors', err);
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  const handleVendorCreated = (vendor) => {
    setVendors((prev) => [...prev, vendor]);
    setAddStockForm((prev) => ({ ...prev, vendor_id: String(vendor.id) }));
    setAddVendorOpen(false);
  };

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(`/instruments/batches/${encodeURIComponent(instrumentName)}`);
      const fetched = response.data.batches || [];
      setBatches(fetched);
      if (fetched.length > 0) {
        await fetchHistory(fetched);
      } else {
        setHistory([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  }, [instrumentName, fetchHistory]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    if ((showAddStock || editingBatch) && vendors.length === 0) {
      fetchVendors();
    }
  }, [showAddStock, editingBatch, vendors.length, fetchVendors]);

  const handleAddStockSubmit = async (e) => {
    e.preventDefault();
    const qty = parseInt(addStockForm.quantity, 10);
    if (Number.isNaN(qty) || qty < 1) {
      setError('Quantity must be at least 1');
      return;
    }
    setAddStockSubmitting(true);
    try {
      await axiosInstance.post('/instruments', {
        instrument_name: instrumentName,
        purchase_date: addStockForm.purchase_date,
        quantity: qty,
        cost: addStockForm.cost !== '' ? addStockForm.cost : null,
        vendor_id: addStockForm.vendor_id || null,
        brand: addStockForm.brand || null,
        make: addStockForm.make || null,
        serial_no: addStockForm.serial_no || null
      });
      setShowAddStock(false);
      setAddStockForm({
        purchase_date: today(),
        quantity: '',
        cost: '',
        brand: '',
        make: '',
        serial_no: '',
        vendor_id: ''
      });
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add stock');
    } finally {
      setAddStockSubmitting(false);
    }
  };

  const openEdit = (batch) => {
    setEditingBatch(batch);
    setEditForm({
      instrument_name: instrumentName,
      purchase_date: batch.purchase_date ? batch.purchase_date.slice(0, 10) : '',
      cost: batch.cost != null ? String(batch.cost) : '',
      brand: batch.brand || '',
      make: batch.make || '',
      serial_no: batch.serial_no || '',
      vendor_id: batch.vendor_id != null ? String(batch.vendor_id) : ''
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingBatch) return;
    setEditSubmitting(true);
    try {
      await axiosInstance.put(`/instruments/${editingBatch.id}`, {
        instrument_name: editForm.instrument_name?.trim() || instrumentName,
        purchase_date: editForm.purchase_date,
        cost: editForm.cost !== '' ? editForm.cost : null,
        brand: editForm.brand || null,
        make: editForm.make || null,
        serial_no: editForm.serial_no || null,
        vendor_id: editForm.vendor_id || null
      });
      setEditingBatch(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update batch');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openStatusModal = (batch) => {
    setStatusModalBatch(batch);
    setStatusForm({
      fromStatus: 'in_stock',
      toStatus: 'in_use',
      quantity: '1',
      changeDate: today(),
      remarks: ''
    });
    setError('');
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    if (!statusModalBatch) return;
    const qty = parseInt(statusForm.quantity, 10);
    if (Number.isNaN(qty) || qty < 1) {
      setError('Quantity must be at least 1');
      return;
    }
    setStatusSubmitting(true);
    try {
      await axiosInstance.put(`/instruments/${statusModalBatch.id}/status`, {
        fromStatus: statusForm.fromStatus,
        toStatus: statusForm.toStatus,
        quantity: qty,
        changeDate: statusForm.changeDate,
        remarks: statusForm.remarks || null
      });
      setStatusModalBatch(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleDeleteHistory = async () => {
    if (!historyDeleteConfirm) return;
    try {
      await axiosInstance.delete(
        `/instruments/${historyDeleteConfirm.instrument_id}/history/${historyDeleteConfirm.id}`
      );
      setHistoryDeleteConfirm(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete history');
    }
  };

  const bucketSummary = (batch) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, fontSize: '0.85rem' }}>
      <span>Use: {batch.in_use_qty}</span>
      <span>Missed: {batch.missed_qty}</span>
      <span>Broken: {batch.broken_qty}</span>
      <span>Discarded: {batch.discarded_qty}</span>
    </Box>
  );

  const detailBody = (
    <>
        {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button onClick={() => setShowAddStock(true)}>Add Stock</Button>
        </Box>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <>
            <Card sx={{ mb: 3, p: 2 }}>
              <Typography level="title-md" sx={{ mb: 1 }}>Batches</Typography>
              <ResponsiveDetailTable
                mobile={isPage ? (
                  batches.length === 0 ? (
                    <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>No batches</Typography>
                  ) : (
                    batches.map((batch) => (
                      <DetailFieldCard
                        key={batch.id}
                        title={[batch.brand, batch.make].filter(Boolean).join(' / ') || 'Batch'}
                        subtitle={batch.serial_no || undefined}
                        fields={[
                          { label: 'Purchase Date', value: formatDisplayDate(batch.purchase_date) },
                          { label: 'Qty Purchased', value: batch.quantity_purchased },
                          { label: 'In Stock', value: batch.in_stock_qty ?? 0 },
                          { label: 'Status', value: bucketSummary(batch) },
                          { label: 'Cost', value: batch.cost != null ? `₹${batch.cost}` : '—' },
                          { label: 'Vendor', value: batch.vendor_name || '—' }
                        ]}
                        actions={(
                          <>
                            <Button size="sm" variant="outlined" onClick={() => openEdit(batch)}>Edit</Button>
                            <Button size="sm" onClick={() => openStatusModal(batch)}>Update Status</Button>
                          </>
                        )}
                      />
                    ))
                  )
                ) : null}
              >
              <Table size="sm" sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
                <thead>
                  <tr>
                    <th>Purchase Date</th>
                    <th>Qty</th>
                    <th>In Stock</th>
                    <th>Status Qty</th>
                    <th>Cost</th>
                    <th>Vendor</th>
                    <th>Brand / Make</th>
                    <th>Serial</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((batch) => (
                    <tr key={batch.id}>
                      <td>{formatDisplayDate(batch.purchase_date)}</td>
                      <td>{batch.quantity_purchased}</td>
                      <td>{batch.in_stock_qty ?? 0}</td>
                      <td>{bucketSummary(batch)}</td>
                      <td>{batch.cost != null ? `₹${batch.cost}` : '—'}</td>
                      <td>{batch.vendor_name || '—'}</td>
                      <td>{[batch.brand, batch.make].filter(Boolean).join(' / ') || '—'}</td>
                      <td>{batch.serial_no || '—'}</td>
                      <td>
                        <Button size="sm" variant="outlined" sx={{ mr: 0.5 }} onClick={() => openEdit(batch)}>Edit</Button>
                        <Button size="sm" onClick={() => openStatusModal(batch)}>Update Status</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </ResponsiveDetailTable>
            </Card>

            <Card sx={{ p: 2 }}>
              <Typography level="title-md" sx={{ mb: 1 }}>Status History</Typography>
              <ResponsiveDetailTable
                mobile={isPage ? (
                  history.length === 0 ? (
                    <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>No history</Typography>
                  ) : (
                    history.map((h) => (
                      <DetailFieldCard
                        key={h.id}
                        title={formatDisplayDate(h.change_date)}
                        fields={[
                          { label: 'From', value: h.from_status ? STATUS_LABELS[h.from_status] : '—' },
                          { label: 'To', value: STATUS_LABELS[h.to_status] || h.to_status },
                          { label: 'Qty', value: h.quantity },
                          {
                            label: 'Rate',
                            value: h.rate != null
                              ? Number(h.rate).toLocaleString(undefined, { minimumFractionDigits: 2 })
                              : '—'
                          },
                          { label: 'Remarks', value: h.remarks || '—' },
                          { label: 'By', value: h.created_by_name || '—' }
                        ]}
                        actions={h.from_status != null ? (
                          <Button size="sm" color="danger" variant="plain" onClick={() => setHistoryDeleteConfirm(h)}>Delete</Button>
                        ) : null}
                      />
                    ))
                  )
                ) : null}
              >
              <Table size="sm" sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Remarks</th>
                    <th>By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center' }}>No history</td></tr>
                  ) : (
                    history.map((h) => (
                      <tr key={h.id}>
                        <td>{formatDisplayDate(h.change_date)}</td>
                        <td>{h.from_status ? STATUS_LABELS[h.from_status] : '—'}</td>
                        <td>{STATUS_LABELS[h.to_status] || h.to_status}</td>
                        <td>{h.quantity}</td>
                        <td>
                          {h.rate != null
                            ? Number(h.rate).toLocaleString(undefined, { minimumFractionDigits: 2 })
                            : '—'}
                        </td>
                        <td>{h.remarks || '—'}</td>
                        <td>{h.created_by_name || '—'}</td>
                        <td>
                          {h.from_status != null && (
                            <Button size="sm" color="danger" variant="plain" onClick={() => setHistoryDeleteConfirm(h)}>
                              Delete
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              </ResponsiveDetailTable>
            </Card>
          </>
        )}
    </>
  );

  return (
    <>
      {isPage ? (
        <StockDetailLayout title={instrumentName} onBack={onClose}>
          {detailBody}
        </StockDetailLayout>
      ) : (
        <Modal open onClose={onClose}>
          <ModalDialog sx={{ width: '95%', maxWidth: 1200, maxHeight: '90vh', overflow: 'auto' }}>
            <ModalClose />
            <Typography level="h3" sx={{ mb: 2 }}>{instrumentName}</Typography>
            {detailBody}
          </ModalDialog>
        </Modal>
      )}

        {/* Add stock modal */}
        <Modal open={showAddStock} onClose={() => setShowAddStock(false)}>
          <ModalDialog>
            <ModalClose />
            <Typography level="h4">Add Stock</Typography>
            <form onSubmit={handleAddStockSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>Purchase Date</FormLabel>
                <Input type="date" value={addStockForm.purchase_date} onChange={(e) => setAddStockForm((p) => ({ ...p, purchase_date: e.target.value }))} required />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Quantity</FormLabel>
                <Input type="number" min={1} value={addStockForm.quantity} onChange={(e) => setAddStockForm((p) => ({ ...p, quantity: e.target.value }))} required />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Cost</FormLabel>
                <Input type="number" value={addStockForm.cost} onChange={(e) => setAddStockForm((p) => ({ ...p, cost: e.target.value }))} />
              </FormControl>
              <VendorSelect
                vendors={vendors}
                value={addStockForm.vendor_id || ''}
                onChange={(vendorId) => setAddStockForm((p) => ({ ...p, vendor_id: vendorId }))}
                onAddNew={() => setAddVendorOpen(true)}
                loading={vendorsLoading}
                required
              />
              <FormControl sx={{ mb: 1 }}><FormLabel>Brand</FormLabel>
                <Input value={addStockForm.brand} onChange={(e) => setAddStockForm((p) => ({ ...p, brand: e.target.value }))} />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Make</FormLabel>
                <Input value={addStockForm.make} onChange={(e) => setAddStockForm((p) => ({ ...p, make: e.target.value }))} />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Serial No (optional)</FormLabel>
                <Input value={addStockForm.serial_no} onChange={(e) => setAddStockForm((p) => ({ ...p, serial_no: e.target.value }))} />
              </FormControl>
              <Button type="submit" loading={addStockSubmitting} sx={{ mt: 1 }}>Add</Button>
            </form>
          </ModalDialog>
        </Modal>

        {/* Edit batch modal */}
        <Modal open={!!editingBatch} onClose={() => setEditingBatch(null)}>
          <ModalDialog>
            <ModalClose />
            <Typography level="h4">Edit Batch</Typography>
            <form onSubmit={handleEditSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>Name</FormLabel>
                <Input value={editForm.instrument_name} onChange={(e) => setEditForm((p) => ({ ...p, instrument_name: e.target.value }))} />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Purchase Date</FormLabel>
                <Input type="date" value={editForm.purchase_date} onChange={(e) => setEditForm((p) => ({ ...p, purchase_date: e.target.value }))} required />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Cost</FormLabel>
                <Input type="number" value={editForm.cost} onChange={(e) => setEditForm((p) => ({ ...p, cost: e.target.value }))} />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Vendor</FormLabel>
                <Select value={editForm.vendor_id} onChange={(_, v) => setEditForm((p) => ({ ...p, vendor_id: v || '' }))}>
                  <Option value="">None</Option>
                  {vendors.map((v) => <Option key={v.id} value={String(v.id)}>{v.name}</Option>)}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Brand / Make / Serial</FormLabel>
                <Input placeholder="Brand" value={editForm.brand} onChange={(e) => setEditForm((p) => ({ ...p, brand: e.target.value }))} sx={{ mb: 0.5 }} />
                <Input placeholder="Make" value={editForm.make} onChange={(e) => setEditForm((p) => ({ ...p, make: e.target.value }))} sx={{ mb: 0.5 }} />
                <Input placeholder="Serial" value={editForm.serial_no} onChange={(e) => setEditForm((p) => ({ ...p, serial_no: e.target.value }))} />
              </FormControl>
              <Button type="submit" loading={editSubmitting} sx={{ mt: 1 }}>Save</Button>
            </form>
          </ModalDialog>
        </Modal>

        {/* Status update modal */}
        <Modal open={!!statusModalBatch} onClose={() => setStatusModalBatch(null)}>
          <ModalDialog>
            <ModalClose />
            <Typography level="h4">Update Status</Typography>
            <form onSubmit={handleStatusSubmit}>
              <FormControl sx={{ mb: 1 }}><FormLabel>From Status</FormLabel>
                <Select
                  value={statusForm.fromStatus}
                  onChange={(_, v) => setStatusForm((p) => ({
                    ...p,
                    fromStatus: v,
                    toStatus: ALLOWED_TO[v]?.[0] || p.toStatus
                  }))}
                >
                  {Object.keys(STATUS_LABELS).map((s) => (
                    <Option key={s} value={s}>{STATUS_LABELS[s]}</Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>To Status</FormLabel>
                <Select value={statusForm.toStatus} onChange={(_, v) => setStatusForm((p) => ({ ...p, toStatus: v }))}>
                  {(ALLOWED_TO[statusForm.fromStatus] || []).map((s) => (
                    <Option key={s} value={s}>{STATUS_LABELS[s]}</Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Quantity</FormLabel>
                <Input type="number" min={1} value={statusForm.quantity} onChange={(e) => setStatusForm((p) => ({ ...p, quantity: e.target.value }))} required />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Date</FormLabel>
                <Input type="date" value={statusForm.changeDate} onChange={(e) => setStatusForm((p) => ({ ...p, changeDate: e.target.value }))} required />
              </FormControl>
              <FormControl sx={{ mb: 1 }}><FormLabel>Remarks</FormLabel>
                <Textarea value={statusForm.remarks} onChange={(e) => setStatusForm((p) => ({ ...p, remarks: e.target.value }))} minRows={2} />
              </FormControl>
              <Button type="submit" loading={statusSubmitting} sx={{ mt: 1 }}>Update</Button>
            </form>
          </ModalDialog>
        </Modal>

        {/* Delete history confirm */}
        <Modal open={!!historyDeleteConfirm} onClose={() => setHistoryDeleteConfirm(null)}>
          <ModalDialog>
            <Typography>Delete this history record and reverse the status move?</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button color="danger" onClick={handleDeleteHistory}>Delete</Button>
              <Button variant="outlined" onClick={() => setHistoryDeleteConfirm(null)}>Cancel</Button>
            </Box>
          </ModalDialog>
        </Modal>

        <AddVendorModal
          open={addVendorOpen}
          onClose={() => setAddVendorOpen(false)}
          onCreated={handleVendorCreated}
        />
    </>
  );
};

export default InstrumentPopup;
